/**
 * UI strings (Concept §8.3): `src/i18n/{en,de}.json`; content texts come from the data files.
 */
import { derived, get, writable } from 'svelte/store';
import de from './de.json';
import en from './en.json';

export const LANGUAGES = ['en', 'de'] as const;
export type Lang = (typeof LANGUAGES)[number];
export type MessageKey = keyof typeof en;

const MESSAGES: Record<Lang, Record<MessageKey, string>> = { en, de };

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/** Picks the UI language from the browser's preferred languages (fallback: English). */
export function detectLanguage(preferred: readonly string[] = globalThis.navigator?.languages ?? []): Lang {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'en';
}

export const lang = writable<Lang>(detectLanguage());

export function translate(language: Lang, key: MessageKey, params?: Record<string, string>): string {
  let text = MESSAGES[language][key] ?? MESSAGES.en[key] ?? key;
  if (params) for (const [k, v] of Object.entries(params)) text = text.replaceAll(`{${k}}`, v);
  return text;
}

/** `$t('time.play')` in components. */
export const t = derived(lang, (l) => (key: MessageKey, params?: Record<string, string>) => translate(l, key, params));

/** Localized content text ({ en, de }). */
export function localized(text: { en: string; de: string }, language: Lang = get(lang)): string {
  return text[language];
}

export function setLang(l: Lang): void {
  lang.set(l);
  document.documentElement.lang = l;
}
